import React, { useCallback, useRef, useState } from 'react'
import { Breadcrumbs, CreateButton, DynamicEntityIndex, FlashMessage, GlobalLoader, InputField, Modal, ModalAlert, SelectInput } from '@reactiveonline/reactive_admin_shared-packages'
import debounce from 'lodash.debounce'
import GlobalImports from '../shared/GlobalImports'

const accountTableHeadings = [
  { type: 'string',             label: 'storeName'       },
  { type: 'string',             label: 'flavour'         },
  { type: 'string',             label: 'onboardingAgent' },
  { type: 'date',               label: 'assignedOn'      },
  { key:  'unassign_button' },
]

export default function Index({
  appProps, onboardingAuthorizations, assignAgentPath, breadcrumbs, entityPath, icon, initialTotalCount, initialTotalPages, onboardingAgents, searchAccountsPath, unassignAgentPath
}) {
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [searchText, setSearchText] = useState('')
  const [relevantAccounts, setRelevantAccounts] = useState([])
  const [currentlySelectedAccount, setCurrentlySelectedAccount] = useState(null)
  const [currentlySelectedAgent, setCurrentlySelectedAgent] = useState(null)
  const [onboardingToDelete, setOnboardingToDelete] = useState(null)
  const [hoveredItem, setHoveredItem] = useState({ type: '', id: null })
  const [loading, setLoading] = useState(false)

  const flashMessageRef = useRef(null)

  const filters = [
    {
      key: 'flavour',
      title: 'Flavour',
      type: 'select',
      options: ['Shop', 'Ordering', 'Appointment', 'Travel'].map(item => (
        {
          title: Support.translations.module_names[item.toLowerCase()],
          value: item
        }
      ))
    },
    {
      key: 'onboarding_agent_id',
      title: Support.translations.columns.onboardingAgent,
      type: 'select',
      options: onboardingAgents
    }
  ]

  const closeCreateModal = () => {
    setSearchText('')
    setShowCreateModal(false)
    setRelevantAccounts([])
    setCurrentlySelectedAccount(null)
    setCurrentlySelectedAgent(null)
  }

  const getRelevantAccounts = searchValue => {
    const url = `${ searchAccountsPath }?search=${ searchValue }`

    setLoading(true)

    Rails.ajax({
      type: 'GET',
      url: url,
      dataType: 'json',
      success: res => {
        setRelevantAccounts(res.accounts)
        setLoading(false)
      },
      error: _res => setLoading(false)
    })
  }

  function handleSearch(value) {
    setSearchText(value)

    if (value.length > 2) {
      debouncedSearch(value)
    } else {
      setRelevantAccounts([])
      debouncedSearch.cancel()
    }
  }

  const debouncedSearch = useCallback(
    debounce(value => getRelevantAccounts(value), 1000), []
  )

  const handleSelectAccount = account => {
    setSearchText('')
    setRelevantAccounts([])
    setCurrentlySelectedAccount(account)
  }

  const checkIfActionShouldBeDisabled = () => {
    if (!currentlySelectedAccount || (!currentlySelectedAgent || currentlySelectedAgent.value === -1)) {
      return true
    }

    return currentlySelectedAccount.onboarding_agent_id === currentlySelectedAgent.value
  }

  const handleClearSelectedAccount = _e => {
    setCurrentlySelectedAccount(null)
    setCurrentlySelectedAgent(null)
  }

  const itemRenderer = (item, columns) => {
    return (
      <>
        { columns.map((column, index) => {
          if ( ['storeName', 'flavour', 'assignedOn'].includes(column.label)) {
            return (
              <div key={ column.label } className='item flex-1'>
                { item[column.label] }
              </div>
            )
          } else if (column.label === 'onboardingAgent') {
            const exceedsLength = item[column.label].length > 30

            return (
              <div
                key={ column.label }
                className='item flex-1'
                onMouseEnter={ _e => setHoveredItem({ type: column.label, id: item.id })}
                onMouseLeave={ _e => setHoveredItem({ type: '', id: null })}
                style={{ position: 'relative' }}
              >
                <div>
                  { exceedsLength ? item[column.label].slice(0, 27) + '...' : item[column.label] }
                </div>

                { exceedsLength && hoveredItem.type === column.label && hoveredItem.id === item.id &&
                  <div
                    className='flex-box items-center p-r-10'
                    style={{ position: 'absolute', backgroundColor: 'rgba(255, 255, 255, 1)', zIndex: 99999, height: '100%' }}
                  >
                    { item[column.label] }
                  </div>
                }
              </div>
            )
          } else if (column.key === 'unassign_button') {
            return (
              <div key={ index } className='item flex-1'>
                <div
                  className='btn content-center'
                  style={{ gap: '5px' }}
                  onClick={ _e => setOnboardingToDelete(item) }
                >
                  <i className='fa-light fa-headset' />
                  <span>{ Support.translations.onboardings.unassign_agent }</span>
                </div>
              </div>
            )
          }
        })}
      </>
    )
  }

  const handleAssignAgent = () => {
    const fd = new FormData()

    fd.append('onboarding_agent_id', currentlySelectedAgent.value)
    fd.append('account_id', currentlySelectedAccount.id)

    setLoading(true)

    Rails.ajax({
      type: 'POST',
      url: assignAgentPath,
      dataType: 'json',
      data: fd,
      success: _res => window.location.reload(),
      error: res => {
        flashMessageRef.current.show(res.error, 'error')
        setLoading(false)
      }
    })
  }

  const handleUnassignAgent = () => {
    const fd = new FormData()

    fd.append('onboarding_agent_id', onboardingAgents.find(el => el.title === onboardingToDelete.onboardingAgent)?.value)
    fd.append('account_id', onboardingToDelete.accountId)

    setOnboardingToDelete(null)
    setLoading(true)

    Rails.ajax({
      type: 'POST',
      url: unassignAgentPath,
      dataType: 'json',
      data: fd,
      success: _res => window.location.reload(),
      error: res => {}
    })
  }

  return (
    <>
      <div className='page-wrapper-header'>
        <div>
          <div className='page-header'>
            <i className={ `fa-light ${ icon }` }/>
            <h1 className='page-header-title'>
              { Support.translations.onboardings.onboardings }
            </h1>
          </div>

          <Breadcrumbs
            breadcrumbs={ breadcrumbs }
            wrapperHeader={ false }
          />
        </div>

        <CreateButton
          onClickCreate={ () => setShowCreateModal(true) }
          createTitle={ Support.translations.onboardings.assign_agent }
        />
      </div>

      <DynamicEntityIndex
        initialItems={ onboardingAuthorizations }
        initialTotalCount={ initialTotalCount }
        initialTotalPages={ initialTotalPages }
        disableCustomization={ true }
        disableDelete={ true }
        entity='onboarding_agent_authorizations'
        headline={ Support.translations.onboardings.onboardings }
        tableHeadings={ accountTableHeadings }
        itemRenderer={ itemRenderer }
        itemsPath={ entityPath }
        icon={ icon }
        noResourcesTitle={ Support.translations.account.no_accounts }
        listColumns={ accountTableHeadings }
        columnTranslations={ Support.translations.columns }
        filters={ filters }
        disableUrlUpdates
      />

      { showCreateModal &&
        <Modal
          visible
          mode='large'
          closeModal={ closeCreateModal }
          action={ handleAssignAgent }
          actionText={ Support.translations.onboardings.assign_agent }
          disableAction={ checkIfActionShouldBeDisabled() }
        >
          <div>
            { currentlySelectedAccount &&
              <div className='flex-box content-space-between'>
                <InputField
                  className='col s12'
                  label={ Support.translations.onboardings.selected_account }
                >
                  <div
                    className='flex-box items-start content-end m-t-15'
                    style={{ height: '40px' }}
                  >
                    <div
                      className='btn plain flex-box p-l-0'
                      onClick={ handleClearSelectedAccount }
                      style={{ cursor: 'pointer' }}
                    >
                      <div>
                        { `${ currentlySelectedAccount.domain } (${ currentlySelectedAccount.name })` }
                      </div>
                      <i className='fa-light fa-x m-l-10' />
                    </div>
                  </div>
                </InputField>

                <div style={{ flexBasis: '200px' }}>
                  <InputField
                    className='col s12'
                    label={ Support.translations.onboardings.selected_agent }
                  >
                    <SelectInput
                      options={ [{ title: '-', value: -1 }, ...onboardingAgents] }
                      currentOption={ currentlySelectedAgent?.value ?? -1 }
                      setCurrentOption={ option => setCurrentlySelectedAgent(option) }
                    />
                  </InputField>
                </div>
              </div>
            }

            { !currentlySelectedAccount &&
              <>
                <InputField
                  className='col s12'
                  label={ Support.translations.onboardings.tenant_search }
                >
                  <input
                    type="search"
                    autoComplete="on"
                    placeholder={ Support.translations.onboardings.search_for_tenant }
                    value={ searchText }
                    onChange={ e => handleSearch(e.target.value) }
                  />
                </InputField>

                { !!relevantAccounts.length &&
                  <table>
                    <thead>
                      <tr>
                        <th>{ Support.translations.account.model_name }</th>
                        <th>{ Support.translations.tenant.flavour }</th>
                      </tr>
                    </thead>
                    <tbody>
                      { relevantAccounts.map((account, idx) => (
                        <tr
                          key={ idx }
                          onClick={ _e => handleSelectAccount(account) }
                          style={{ cursor: 'pointer' }}
                        >
                          <td>{ `${ account.domain } (${ account.name })` }</td>
                          <td>{ account.flavour }</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                }
              </>
            }
          </div>

          { loading && <GlobalLoader fullPage /> }
        </Modal>
      }

      { !!onboardingToDelete &&
        <ModalAlert
          alert={ Support.translations.onboardings.unassign_agent_alert }
          onSelect={ () => handleUnassignAgent(onboardingToDelete) }
          onClose={ () => setOnboardingToDelete(null) }
        />
      }

      <FlashMessage ref={ flashMessageRef } />
      <GlobalImports appProps={ appProps } />

      { loading && <GlobalLoader fullPage /> }
    </>
  )
}